/**
 * Household lifecycle: create, lookup, invite, join.
 *
 * Data model:
 *   users/{uid}                       — { householdId?, displayName?, ... }
 *   households/{householdId}          — { memberIds: string[], createdAt, createdBy }
 *   invites/{code}                    — { householdId, expiresAt, createdBy, status }
 *
 * All shared surfaces (mealPlan, groceryList, inventory, savedRecipes,
 * recipeMetadata, userPreferences) move under households/{householdId}/...
 * once paired. See householdPaths.ts for the routing logic.
 */

import {
  collection,
  getCountFromServer,
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { auth, firestore, isFirebaseEnabled } from './firebaseConfig';
import { apiPost, ApiError, AUTH_REQUIRED_CODE, NETWORK_ERROR_CODE } from './apiClient';
import { isNetworkError } from '../utils/fetchErrors';

export interface UserProfile {
  uid: string;
  householdId?: string | null;
  displayName?: string | null;
  createdAt?: Timestamp | null;
}

export interface Household {
  id: string;
  memberIds: string[];
  createdBy: string;
  createdAt?: Timestamp | null;
  /**
   * Display names of current members, keyed by uid.
   *
   * Lives here rather than being read from users/{uid} because
   * firestore.rules restricts that doc to its owner — a member cannot read
   * their partner's profile. The household doc is already member-readable.
   *
   * Known weakness: the existing update rule lets any member write the whole
   * household doc, including another member's name. Acceptable for a trusted
   * household of two; tighten the rule before allowing larger groups.
   */
  members?: HouseholdRoster;
  /**
   * The one outstanding invitation, mirrored from `invites/{code}`.
   *
   * firestore.rules permits `get` on invites but not `list`, so there is no
   * way to ask "which of my codes is still live". Without this pointer the
   * Share screen can only mint a new code every time it opens — which is what
   * it used to do.
   */
  pendingInvite?: PendingInvite;
}

export interface PendingInvite {
  code: string;
  createdBy: string;
  expiresAt: Timestamp;
}

export interface Invite {
  code: string;
  householdId: string;
  createdBy: string;
  expiresAt: Timestamp;
  status: 'pending' | 'consumed';
}

export class HouseholdError extends Error {
  constructor(
    public code:
      | 'firebase-disabled'
      | 'invite-not-found'
      | 'invite-expired'
      | 'invite-already-consumed'
      | 'household-full'
      | 'already-in-household'
      | 'not-in-household'
      | 'not-authorized'
      | 'migration-failed'
      // The request never landed, or the session token could not be refreshed.
      | 'network'
      // The session is gone: sign in again.
      | 'auth-required'
      // Redux and Firebase auth disagree on who is signed in.
      | 'session-changed'
      // Anything the server or the SDK said that we have no sentence for.
      | 'unknown',
    message: string
  ) {
    super(message);
    this.name = 'HouseholdError';
  }
}

const ensureFirestore = () => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new HouseholdError(
      'firebase-disabled',
      'Firebase is not configured'
    );
  }
  return firestore;
};

/**
 * Read the user's profile doc. Returns null if it doesn't exist yet.
 */
export const getUserProfile = async (
  uid: string
): Promise<UserProfile | null> => {
  const db = ensureFirestore();
  const snapshot = await getDoc(doc(db, 'users', uid));
  if (!snapshot.exists()) return null;
  return { uid, ...(snapshot.data() as Omit<UserProfile, 'uid'>) };
};

/**
 * Read a user's householdId, or null if solo.
 */
export const getUserHouseholdId = async (
  uid: string
): Promise<string | null> => {
  const profile = await getUserProfile(uid);
  return profile?.householdId ?? null;
};

/**
 * Read a household doc by id. Returns null if not found.
 */
export const getHousehold = async (
  householdId: string
): Promise<Household | null> => {
  const db = ensureFirestore();
  const snapshot = await getDoc(doc(db, 'households', householdId));
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  return {
    id: householdId,
    memberIds: data.memberIds ?? [],
    createdBy: data.createdBy,
    createdAt: data.createdAt ?? null,
    members: data.members,
    pendingInvite: data.pendingInvite,
  };
};

/**
 * Watch a household. Used by the Settings row and the household screen so that
 * "Invitation pending" becomes "Shared with Marie" the moment she joins,
 * without the user reloading to find out.
 */
export const subscribeToHousehold = (
  householdId: string,
  onChange: (household: Household | null) => void,
  /**
   * Called when Firestore refuses the read — which, for a household we believe
   * we are in, means one thing: our partner removed us.
   *
   * The listener cannot report this any other way. `households/{id}` is
   * readable only by its members, so the instant a removal lands the
   * subscription stops receiving documents and starts receiving
   * permission-denied. Without this signal the removed member's app goes on
   * writing to `households/{hid}/…` — every write silently rejected, and
   * invisible because the slices are in the redux-persist whitelist — until
   * they happen to reload. See useHouseholdLiveSync for what happens next.
   */
  onDenied?: () => void
): (() => void) => {
  if (!isFirebaseEnabled() || !firestore) return () => {};
  return onSnapshot(
    doc(firestore, 'households', householdId),
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange(null);
        return;
      }
      const data = snapshot.data();
      onChange({
        id: householdId,
        memberIds: data.memberIds ?? [],
        createdBy: data.createdBy,
        createdAt: data.createdAt ?? null,
        members: data.members,
        pendingInvite: data.pendingInvite,
      });
    },
    (error) => {
      if (error?.code === 'permission-denied') onDenied?.();
      onChange(null);
    }
  );
};

export interface RosterEntry {
  displayName: string | null;
  /**
   * Carried alongside the name because Firebase only populates displayName for
   * Google sign-in — an email/password account has none until we ask for one,
   * and an address is a better fallback than a bare uid.
   */
  email?: string | null;
  updatedAt: string;
}

export type HouseholdRoster = Record<string, RosterEntry>;

/**
 * Publish my own identity into the household roster. Each member writes only
 * their own key, via a dotted field path, so two concurrent writes merge
 * instead of clobbering each other.
 */
export const publishMemberProfile = async (
  householdId: string,
  uid: string,
  displayName: string | null,
  email?: string | null
): Promise<void> => {
  const db = ensureFirestore();
  await updateDoc(doc(db, 'households', householdId), {
    [`members.${uid}`]: {
      displayName,
      email: email ?? null,
      updatedAt: new Date().toISOString(),
    },
  });
};

/**
 * Set the signed-in user's display name on their Firebase account, and mirror
 * it into the household roster if they are in one.
 *
 * Firebase leaves displayName empty for email/password signups, so without
 * this a paired household can only ever show two email addresses.
 */
export const setDisplayName = async (
  name: string,
  householdId?: string | null
): Promise<void> => {
  const trimmed = name.trim();
  if (!trimmed) return;

  const user = auth?.currentUser;
  if (!user) throw new HouseholdError('not-authorized', 'Not signed in');

  await updateProfile(user, { displayName: trimmed });
  if (householdId) {
    await publishMemberProfile(householdId, user.uid, trimmed, user.email);
  }
};

/**
 * What to call a member, best effort: their name, else the local part of their
 * email, else null for the caller to render a placeholder.
 */
export const nameFor = (entry: RosterEntry | undefined): string | null => {
  const name = entry?.displayName?.trim();
  if (name) return name;
  const local = entry?.email?.trim().split('@')[0];
  return local || null;
};

/** One-character avatar label. Falls back rather than throwing. */
export const initialOf = (displayName: string | null | undefined): string => {
  const trimmed = (displayName ?? '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
};

/**
 * Strip accents for collision detection. Handles NFD decomposition of diacritics.
 */
const stripAccents = (s: string): string => {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
};

/**
 * Avatar initials, disambiguated when two household members' first initials collide.
 *
 * Returns [myInitial, theirInitial]. Single letter normally; two letters
 * (first uppercase, second lowercase) when initials collide, capped at 2.
 *
 * Collision detection is case- and accent-insensitive: "Émile" and "emily"
 * collide. Normalised casing ensures reliability (first upper, second lower).
 * If two letters still cannot distinguish (e.g., "Alex" and "Alice" both → "Al"),
 * falls back to single letter and lets colour distinguish. Never invents
 * numeric suffixes or extends beyond two characters.
 */
export const initialOfDisambiguated = (
  myName: string | null | undefined,
  theirName: string | null | undefined
): [string, string] => {
  const myTrimmed = (myName ?? '').trim();
  const theirTrimmed = (theirName ?? '').trim();

  const myInitial = myTrimmed ? myTrimmed.charAt(0).toUpperCase() : '?';
  const theirInitial = theirTrimmed ? theirTrimmed.charAt(0).toUpperCase() : '?';

  // No collision if either name is missing or has no first character
  if (!myTrimmed || !theirTrimmed) {
    return [myInitial, theirInitial];
  }

  // Check if initials collide (case- and accent-insensitive)
  const myAccentless = stripAccents(myTrimmed.charAt(0).toLowerCase());
  const theirAccentless = stripAccents(theirTrimmed.charAt(0).toLowerCase());

  if (myAccentless !== theirAccentless) {
    // No collision
    return [myInitial, theirInitial];
  }

  // Initials collide. If names are identical, fall back to single letter.
  if (myTrimmed === theirTrimmed) {
    return [myInitial, theirInitial];
  }

  // Return 2-character prefixes: first uppercase, second lowercase (normalised).
  // Removes dependency on typing habits. Example: "Claude" and "christine"
  // both yield "Cl" and "Ch", even though the input casing differs.
  const myPrefix = myTrimmed.substring(0, Math.min(2, myTrimmed.length));
  const theirPrefix = theirTrimmed.substring(0, Math.min(2, theirTrimmed.length));

  const myFormatted = myPrefix.charAt(0).toUpperCase() + myPrefix.substring(1).toLowerCase();
  const theirFormatted = theirPrefix.charAt(0).toUpperCase() + theirPrefix.substring(1).toLowerCase();

  // Check if the normalised 2-char prefixes are actually different when accent-stripped
  if (stripAccents(myFormatted.toLowerCase()) !== stripAccents(theirFormatted.toLowerCase())) {
    return [myFormatted, theirFormatted];
  }

  // Two-character prefixes are identical when normalised (e.g., "Alex" and "Alice" → "Al").
  // Fall back to single letter; colour carries the distinction.
  return [myInitial, theirInitial];
};

/**
 * Avatar initials for a whole table, disambiguated as a group.
 *
 * initialOfDisambiguated resolves exactly two names against each other, which
 * was all a household of two ever needed. Once children are at the table the
 * set is open-ended and collisions come in clusters — Nina, Noé and Nathan all
 * want "N".
 *
 * Same rules as the pair version, applied per colliding group: one letter
 * normally; two (first upper, second lower) for everyone in a group whose
 * first letters collide; back to one letter for names a two-letter prefix
 * still cannot separate, where colour carries the distinction.
 *
 * Returns initials positionally, so the caller can zip them onto its own list.
 */
export const initialsFor = (
  names: Array<string | null | undefined>
): string[] => {
  const trimmed = names.map((n) => (n ?? '').trim());
  const single = trimmed.map((n) => (n ? n.charAt(0).toUpperCase() : '?'));

  // Group indices by accent-stripped first letter.
  const groups = new Map<string, number[]>();
  trimmed.forEach((name, i) => {
    if (!name) return;
    const key = stripAccents(name.charAt(0));
    const bucket = groups.get(key);
    if (bucket) bucket.push(i);
    else groups.set(key, [i]);
  });

  const result = [...single];

  groups.forEach((indices) => {
    if (indices.length < 2) return;

    const twoChar = indices.map((i) => {
      const name = trimmed[i];
      const prefix = name.substring(0, Math.min(2, name.length));
      return prefix.charAt(0).toUpperCase() + prefix.substring(1).toLowerCase();
    });

    // Only upgrade the ones a second letter actually separates. "Alex" and
    // "Alice" both give "Al", so they stay single-letter rather than pretending
    // to be distinct.
    indices.forEach((idx, k) => {
      const mine = stripAccents(twoChar[k].toLowerCase());
      const collidesStill = twoChar.some(
        (other, j) => j !== k && stripAccents(other.toLowerCase()) === mine
      );
      if (!collidesStill) result[idx] = twoChar[k];
    });
  });

  return result;
};

/**
 * Name a household member RELATIVE TO WHOEVER IS LOOKING.
 *
 * The viewer is always the self label; the other person gets their name.
 * Never interpolate a raw displayName into a sentence — the design prototype
 * shipped exactly that bug: played as Alex, it rendered "Toi cuisine" on
 * Camille's meals.
 *
 * Delegates to nameFor for the non-self case, so it inherits the
 * email-local-part fallback: Firebase only populates displayName for Google
 * sign-in, and an email/password account would otherwise render as '?'.
 */
export const nameForViewer = (
  uid: string,
  viewerUid: string | null,
  roster: HouseholdRoster | undefined,
  selfLabel: string
): string => {
  if (viewerUid && uid === viewerUid) return selfLabel;
  return nameFor(roster?.[uid]) || '?';
};

/** True while an invite is still usable. */
export const isInviteLive = (
  invite: Pick<PendingInvite, 'expiresAt'> | null | undefined
): boolean => !!invite && invite.expiresAt.toMillis() > Date.now();

export type HouseholdStatus = 'solo' | 'pending' | 'paired';

/**
 * Genuinely sharing with someone — not merely holding a householdId.
 *
 * The distinction is the whole point. A household of one is an artefact: the
 * old Share screen created one just by being opened, and cancelling an
 * invitation still leaves one behind. Guards that tested for the id refused
 * those users an invite while the household screen told them they were solo.
 */
export const isGenuinelyPaired = (
  memberIds: string[] | null | undefined,
  myUid: string | null | undefined
): boolean => (memberIds ?? []).some((id) => id !== myUid);

/**
 * What state the pairing is actually in, from the household document.
 *
 * The old UI branched on "does a householdId exist", which was true from the
 * moment the Share screen was opened and so reported a partner who did not
 * exist. Membership is the only thing that means paired; an outstanding,
 * unexpired invite is its own state; everything else is solo — including a
 * household of one whose invitation has lapsed.
 */
export const householdStatusOf = (
  household: Pick<Household, 'memberIds' | 'pendingInvite'> | null | undefined,
  myUid: string | null | undefined
): HouseholdStatus => {
  if (!household) return 'solo';
  if (isGenuinelyPaired(household.memberIds, myUid)) return 'paired';
  return isInviteLive(household.pendingInvite) ? 'pending' : 'solo';
};

/**
 * Return the household's live invitation, creating the household and the
 * invitation only when they are missing. This is what the Share screen calls.
 *
 * Runs on the server (POST /api/household/invite, api/_lib/householdInvite.ts).
 * It used to run here: read the household back, check the seat, write
 * `invites/{code}`, point the household at it. The read-back is what failed on
 * a brand-new household (#776). The Share screen was writing its roster entry
 * to the same document at the same moment, and Firestore's web SDK, holding an
 * acknowledged write for a document it had never read, dropped the server's
 * slightly older read as outdated and answered "does not exist".
 *
 * The answer names the household the server used, which comes from the
 * profile, so the caller adopts it rather than trusting its own copy.
 */
export const ensureInvite = async (uid: string): Promise<EnsuredInvite> => {
  try {
    // `uid` is sent so the server can reject a mismatch against the token.
    const result = await apiPost<{
      householdId: string;
      invite: { code: string; expiresAt: number };
      householdCreated: boolean;
      migrated: MigrationSummary | null;
    }>('/api/household/invite', { uid });
    return {
      householdId: result.householdId,
      householdCreated: !!result.householdCreated,
      migrated: result.migrated ?? null,
      invite: {
        code: result.invite.code,
        householdId: result.householdId,
        createdBy: uid,
        expiresAt: Timestamp.fromMillis(result.invite.expiresAt),
        status: 'pending',
      },
    };
  } catch (err) {
    throw toHouseholdError(err);
  }
};

export interface EnsuredInvite {
  householdId: string;
  householdCreated: boolean;
  migrated: MigrationSummary | null;
  invite: Invite;
}

/**
 * Withdraw the outstanding invitation: the code and the pointer, in one server
 * transaction. Safe to call when nothing is pending.
 */
export const revokeInvite = async (uid: string): Promise<void> => {
  try {
    await apiPost('/api/household/revoke-invite', { uid });
  } catch (err) {
    throw toHouseholdError(err);
  }
};

/**
 * Look up an invite by code without consuming it. Used to preview the
 * household before the invitee taps "Join".
 */
export const lookupInvite = async (code: string): Promise<Invite | null> => {
  const db = ensureFirestore();
  const snapshot = await getDoc(doc(db, 'invites', code));
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  return {
    code,
    householdId: data.householdId,
    createdBy: data.createdBy,
    expiresAt: data.expiresAt,
    status: data.status,
  };
};

/**
 * Join a household by invite code.
 *
 * This runs on the server (POST /api/household/join), not here. firestore.rules
 * gates `households/{id}` on membership, so an invitee — who is not a member
 * yet — had every read and write in the old client-side transaction denied.
 * That is why production accumulated 26 invite codes and zero joins. See
 * api/household/join.ts for why loosening the rule was the wrong fix.
 *
 * The server owns the invariants (cap, expiry, single-use, one household per
 * user) and returns the resulting roster so the caller can name the partner.
 */
export const joinHouseholdViaCode = async (
  code: string,
  uid: string,
  dataChoice: 'merge' | 'fresh' = 'merge'
): Promise<JoinedHousehold> => {
  try {
    // `uid` is sent so the server can reject a mismatch against the bearer
    // token. Redux and Firebase auth can disagree for a moment after an
    // account switch, and joining as the wrong account is unrecoverable.
    //
    // `dataChoice` is handled server-side too. It used to run on the client
    // after the join returned, which left a closed tab as a half-migrated
    // user — and it read users/{uid}/…, the wrong source for a joiner who was
    // alone in a household of their own.
    const result = await apiPost<{
      householdId: string;
      members: HouseholdMemberSummary[];
      migrated: MigrationSummary | null;
      abandonedHouseholdId: string | null;
    }>('/api/household/join', { code, uid, dataChoice });

    return {
      id: result.householdId,
      memberIds: result.members.map((m) => m.uid),
      members: result.members,
      migrated: result.migrated ?? null,
      abandonedHouseholdId: result.abandonedHouseholdId ?? null,
    };
  } catch (err) {
    throw toHouseholdError(err);
  }
};

/** Per-collection counts of what the join carried across. */
export interface MigrationSummary {
  savedRecipes: number;
  recipeMetadata: number;
  inventory: number;
  mealPlan: number;
  cookLog: number;
  groceryCustomItems: number;
}

/** The join endpoint's view of one member. */
export interface HouseholdMemberSummary {
  uid: string;
  displayName: string | null;
  email: string | null;
  isSelf: boolean;
}

export interface JoinedHousehold {
  id: string;
  memberIds: string[];
  members: HouseholdMemberSummary[];
  migrated: MigrationSummary | null;
  abandonedHouseholdId: string | null;
}

/**
 * Map a failed join response back onto HouseholdError so existing callers keep
 * branching on `code`. apiPost throws a plain Error carrying the server's
 * `error` string, so the machine code is recovered by matching the message.
 */
const SERVER_CODE_TO_HOUSEHOLD_ERROR: Record<string, HouseholdError['code']> = {
  'invite-not-found': 'invite-not-found',
  'invite-expired': 'invite-expired',
  'invite-already-consumed': 'invite-already-consumed',
  'household-not-found': 'invite-not-found',
  'household-full': 'household-full',
  'already-in-household': 'already-in-household',
  'already-a-member': 'already-in-household',
  'invalid-code': 'invite-not-found',
  // The exit codes (#604). `cannot-remove-self`, `target-not-a-member` and
  // `invalid-target` all mean the client asked to remove somebody who is not
  // the partner it is showing, which is a bug here rather than a state the
  // user can act on — they collapse onto not-authorized deliberately.
  'not-in-household': 'not-in-household',
  'not-a-member': 'not-in-household',
  'target-not-a-member': 'not-authorized',
  'invalid-target': 'not-authorized',
  'cannot-remove-self': 'not-authorized',
  // household/create: the copy failed and nothing changed, so a retry is safe.
  'migration-failed': 'migration-failed',
  // household/invite (#776).
  'invite-code-exhausted': 'unknown',
  // Every route.
  [AUTH_REQUIRED_CODE]: 'auth-required',
  unauthorized: 'auth-required',
  [NETWORK_ERROR_CODE]: 'network',
  'uid-mismatch': 'session-changed',
};

export const toHouseholdError = (err: unknown): HouseholdError => {
  if (err instanceof HouseholdError) return err;
  const message = err instanceof Error ? err.message : String(err);
  if (isNetworkError(err)) return new HouseholdError('network', message);
  const serverCode = err instanceof ApiError ? err.code : undefined;
  const mapped = serverCode
    ? SERVER_CODE_TO_HOUSEHOLD_ERROR[serverCode]
    : undefined;
  if (mapped) return new HouseholdError(mapped, message);
  if (err instanceof ApiError && err.status === 401) {
    return new HouseholdError('auth-required', message);
  }
  // Not 'not-authorized': that is a claim about permission, and an
  // unrecognised failure is no such thing.
  return new HouseholdError('unknown', message);
};

/**
 * The domain an invite link should point at when we are not on a dev machine.
 *
 * Deliberately not `window.location.origin` everywhere: a link created from a
 * Vercel preview would then point at that preview, which is torn down, while
 * the invite it carries stays valid for 24 hours.
 */
const CANONICAL_ORIGIN = 'https://duchef.app';

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

/**
 * Build a deep link for an invite code. Used by SharePartnerScreen.
 *
 * On a dev machine the app is served over plain HTTP on an arbitrary port, so
 * a link to the production domain opens production — or, if you correct the
 * host by hand and keep the scheme, fails the TLS handshake against a server
 * that speaks HTTP. Point at wherever the app is actually running instead.
 */
export const buildInviteLink = (code: string): string => {
  const origin =
    typeof window !== 'undefined' &&
    LOCAL_HOSTNAMES.has(window.location?.hostname ?? '')
      ? window.location.origin
      : CANONICAL_ORIGIN;

  return `${origin}/join/${code}`;
};

/**
 * Per-subcollection counts of what a departure copied back into personal
 * scope. Open-ended because the server enumerates whatever the household
 * actually holds rather than a fixed list — see
 * api/_lib/householdDataMigration.ts.
 */
export type RestoreSummary = Record<string, number>;

export interface LeftHousehold {
  householdId: string | null;
  departedUid: string;
  restored: RestoreSummary;
  remainingMemberIds: string[];
  householdDeleted: boolean;
}

/**
 * Leave the household, or remove the other member from it.
 *
 * Runs on the server (POST /api/household/leave, /api/household/remove-partner)
 * for the same reason the join does, and one more. The join could not be done
 * client-side because rules deny a non-member; the exit cannot be done
 * client-side because the data has to be copied out of `households/{hid}/…`
 * back into `users/{uid}/…` before scope flips — pairing copies (migrateScopeIntoHousehold)
 * rather than moves, so the personal path holds nothing newer than pairing day
 * — and because the rules no longer let a member touch memberIds at all.
 *
 * The caller is responsible for flipping Redux scope and re-pulling
 * afterwards; useHouseholdExit does both, and checks the result.
 */
const exitHousehold = async (
  action: 'leave' | 'remove-partner',
  uid: string,
  partnerUid?: string
): Promise<LeftHousehold> => {
  try {
    // `uid` is sent so the server can reject a mismatch against the bearer
    // token, exactly as the join does: Redux and Firebase auth can disagree
    // for a moment after an account switch, and leaving as the wrong account
    // is not undoable.
    const result = await apiPost<LeftHousehold>(`/api/household/${action}`, {
      uid,
      ...(partnerUid ? { partnerUid } : {}),
    });
    return {
      householdId: result.householdId ?? null,
      departedUid: result.departedUid,
      restored: result.restored ?? {},
      remainingMemberIds: result.remainingMemberIds ?? [],
      householdDeleted: result.householdDeleted ?? false,
    };
  } catch (err) {
    throw toHouseholdError(err);
  }
};

/** Leave the household I am in, taking a full copy of everything with me. */
export const leaveHousehold = (uid: string): Promise<LeftHousehold> =>
  exitHousehold('leave', uid);

/**
 * Remove the other member. They keep a full copy — nothing is destroyed by a
 * removal, which is why either member may do it rather than only `createdBy`.
 */
export const removePartner = (
  uid: string,
  partnerUid: string
): Promise<LeftHousehold> => exitHousehold('remove-partner', uid, partnerUid);

/**
 * How much is actually in the household, for the confirmation sheet.
 *
 * The fear that makes pairing feel like a one-way door is losing this, so the
 * sheet names it — "226 recipes, 197 ratings, 721 pantry items" — rather than
 * warning in the abstract. Counted from the household itself rather than from
 * Redux: the store holds what this device has loaded, and a promise about
 * somebody's library should not be a guess.
 *
 * Aggregation queries, so the cost is one read per thousand documents rather
 * than one per document. Each surface degrades to 0 on its own: a number we
 * could not fetch is a line the sheet omits, never a reason the exit is
 * unavailable.
 */
export interface HouseholdContents {
  savedRecipes: number;
  recipeMetadata: number;
  inventory: number;
  mealPlan: number;
}

export const countHouseholdContents = async (
  householdId: string
): Promise<HouseholdContents> => {
  const db = ensureFirestore();

  const countOf = async (sub: string): Promise<number> => {
    try {
      const snap = await getCountFromServer(
        collection(db, 'households', householdId, sub)
      );
      return snap.data().count;
    } catch {
      return 0;
    }
  };

  const [savedRecipes, recipeMetadata, inventory, mealPlan] = await Promise.all([
    countOf('savedRecipes'),
    countOf('recipeMetadata'),
    countOf('inventory'),
    countOf('mealPlan'),
  ]);

  return { savedRecipes, recipeMetadata, inventory, mealPlan };
};
